import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {forkJoin, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {
  DataExportDestinationDto,
  DataExportFormatDto,
  DataExportMappingDto,
  DataExportMappingWriteDto,
  DataImportFormatDto,
  DataImportMappingDto,
  DataModuleSettingsDto,
  DataModuleUsageDto,
  ParamAuditLogDto,
  ParamRuleDto,
  ParamTableFieldDto,
  RefDataModuleDto,
  SelectParamsPostSingleDto,
  SelectParamsPutDto
} from './parametre-donnees-api.models';

function unwrapArray<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) {
    return raw as T[];
  }
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const cand =
      o['data'] ??
      o['rows'] ??
      o['items'] ??
      o['results'] ??
      o['modules'] ??
      o['ref_data_modules'] ??
      o['param_table_fields'];
    if (Array.isArray(cand)) {
      return cand as T[];
    }
  }
  return [];
}

@Injectable({ providedIn: 'root' })
export class ParametreDonneesApiService {
  private readonly base = `${String(environment.parametrage_url).replace(/\/$/, '')}/api/parametrage`;

  constructor(private readonly http: HttpClient) {}

  /** GET ref_data_module */
  getRefDataModules(): Observable<RefDataModuleDto[]> {
    return this.http
      .get<unknown>(`${this.base}/data-modules`)
      .pipe(map((raw) => unwrapArray<RefDataModuleDto>(raw)));
  }

  /** GET data_module_settings */
  getDataModuleSettings(stationId: number, moduleId: number): Observable<DataModuleSettingsDto | null> {
    const params = new HttpParams().set('station_id', String(stationId)).set('module_id', String(moduleId));
    return this.http.get<unknown>(`${this.base}/module-settings`, { params }).pipe(
      map((raw) => {
        if (raw == null || raw === '') {
          return null;
        }
        if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
          const o = raw as Record<string, unknown>;
          if (o['data'] && typeof o['data'] === 'object') {
            return o['data'] as DataModuleSettingsDto;
          }
          return raw as DataModuleSettingsDto;
        }
        return null;
      })
    );
  }

  /** PUT data_module_settings */
  putDataModuleSettings(body: DataModuleSettingsDto): Observable<unknown> {
    return this.http.put<unknown>(`${this.base}/module-settings`, body);
  }

  /** GET data_module_usage */
  getDataModuleUsage(stationId: number, moduleId: number): Observable<DataModuleUsageDto | null> {
    const params = new HttpParams().set('station_id', String(stationId)).set('module_id', String(moduleId));
    return this.http.get<unknown>(`${this.base}/module-usage`, { params }).pipe(
      map((raw) => {
        if (raw == null || raw === '') {
          return null;
        }
        if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
          const o = raw as Record<string, unknown>;
          if (o['data'] && typeof o['data'] === 'object') {
            return o['data'] as DataModuleUsageDto;
          }
          return raw as DataModuleUsageDto;
        }
        return null;
      })
    );
  }

  /** PUT data_module_usage */
  putDataModuleUsage(body: DataModuleUsageDto): Observable<unknown> {
    return this.http.put<unknown>(`${this.base}/module-usage`, body);
  }

  /** GET param_table (liste champs) */
  getParamTableFields(stationId: number, userId: number, tableDbId: number): Observable<ParamTableFieldDto[]> {
    const params = new HttpParams()
      .set('station_id', String(stationId))
      .set('user_id', String(userId))
      .set('table_db_id', String(tableDbId));
    return this.http
      .get<unknown>(`${this.base}/param-fields`, { params })
      .pipe(map((raw) => unwrapArray<ParamTableFieldDto>(raw)));
  }

  getParamTableFieldById(id: number): Observable<ParamTableFieldDto> {
    return this.http.get<ParamTableFieldDto>(`${this.base}/param-fields/${id}`);
  }

  postParamTableField(body: Partial<ParamTableFieldDto>): Observable<ParamTableFieldDto> {
    return this.http.post<ParamTableFieldDto>(`${this.base}/param-fields`, body);
  }

  /**
   * POST — une option : body `{ param_table_id, libelle, code?, couleur? }`.
   * `station_id` et `user_id` en query.
   */
  postSelectParamSingle(
    body: SelectParamsPostSingleDto,
    stationId: number,
    userId: number
  ): Observable<unknown> {
    const params = new HttpParams()
      .set('station_id', String(Math.floor(stationId)))
      .set('user_id', String(Math.floor(userId)));
    const url = `${this.base}/select-params`;
    const query = { station_id: String(Math.floor(stationId)), user_id: String(Math.floor(userId)) };
    const bodyLog = JSON.parse(JSON.stringify(body)) as Record<string, unknown>;
    console.log('[Parametrage API] POST select-params', { url, query, body: bodyLog });
    return this.http.post<unknown>(url, body, { params });
  }

  /** Plusieurs options : un POST par ligne (même `param_table_id`). */
  postSelectParamsMany(
    paramTableId: number,
    parts: Array<{ libelle: string; code?: string; couleur?: string }>,
    stationId: number,
    userId: number
  ): Observable<unknown[]> {
    if (parts.length === 0) {
      return of([]);
    }
    return forkJoin(
      parts.map((p) => {
        const body: SelectParamsPostSingleDto = {
          param_table_id: paramTableId,
          libelle: p.libelle
        };
        const code = (p.code || '').trim();
        const couleur = (p.couleur || '').trim();
        if (code) {
          body.code = code;
        }
        if (couleur) {
          body.couleur = couleur;
        }
        return this.postSelectParamSingle(body, stationId, userId);
      })
    );
  }

  /** GET — options existantes pour un param-field (param_table_id). */
  getSelectParamsByParamTable(
    paramTableId: number,
    stationId: number,
    userId: number
  ): Observable<unknown> {
    const path = `${this.base}/select-params/param-table/${encodeURIComponent(String(paramTableId))}`;
    const params = new HttpParams()
      .set('station_id', String(Math.floor(stationId)))
      .set('user_id', String(Math.floor(userId)));
    return this.http.get<unknown>(path, { params });
  }

  /** PUT — met à jour une option (query `station_id`, `user_id`). */
  putSelectParam(
    selectParamId: number,
    body: SelectParamsPutDto,
    stationId: number,
    userId: number
  ): Observable<unknown> {
    const params = new HttpParams()
      .set('station_id', String(Math.floor(stationId)))
      .set('user_id', String(Math.floor(userId)));
    const url = `${this.base}/select-params/${encodeURIComponent(String(selectParamId))}`;
    const query = { station_id: String(Math.floor(stationId)), user_id: String(Math.floor(userId)) };
    console.log('[Parametrage API] PUT select-params/:id', {
      url,
      selectParamId,
      query,
      body: JSON.parse(JSON.stringify(body)) as Record<string, unknown>
    });
    return this.http.put<unknown>(url, body, { params });
  }

  patchParamTableField(id: number, body: Partial<ParamTableFieldDto>): Observable<ParamTableFieldDto> {
    return this.http.put<ParamTableFieldDto>(`${this.base}/param-fields/${id}`, body);
  }

    deleteParamTableField(id: number, stationId: number, userId: number): Observable<unknown> {
        const params = new HttpParams().set('station_id', String(stationId)).set('user_id', String(userId));
        return this.http.delete<unknown>(`${this.base}/param-fields/${id}`, { params });
    }

  getParamRules(stationId: number, tableDbId: number): Observable<ParamRuleDto[]> {
    const params = new HttpParams().set('station_id', String(stationId)).set('table_db_id', String(tableDbId));
    return this.http
      .get<unknown>(`${this.base}/param-rules`, { params })
      .pipe(map((raw) => unwrapArray<ParamRuleDto>(raw)));
  }

  postParamRule(body: Partial<ParamRuleDto>): Observable<ParamRuleDto> {
    return this.http.post<ParamRuleDto>(`${this.base}/param-rules`, body);
  }

  patchParamRule(id: number, body: Partial<ParamRuleDto>): Observable<ParamRuleDto> {
    return this.http.patch<ParamRuleDto>(`${this.base}/param-rules/${id}`, body);
  }

    deleteParamRule(id: number, stationId: number): Observable<unknown> {
        const params = new HttpParams().set('station_id', String(stationId));
        return this.http.delete<unknown>(`${this.base}/param-rules/${id}`, { params });
    }

  getParamAuditLogs(
    stationId: number,
    tableDbId: number,
    q: {
      date_from?: string;
      date_to?: string;
      user_id?: number;
      action?: string;
      objet?: string;
      page?: number;
      page_size?: number;
    }
  ): Observable<{ rows: ParamAuditLogDto[]; total?: number }> {
    let params = new HttpParams().set('station_id', String(stationId)).set('table_db_id', String(tableDbId));
    if (q.date_from) {
      params = params.set('date_from', q.date_from);
    }
    if (q.date_to) {
      params = params.set('date_to', q.date_to);
    }
    if (q.user_id != null) {
      params = params.set('user_id', String(q.user_id));
    }
    if (q.action) {
      params = params.set('action', q.action);
    }
    if (q.objet) {
      params = params.set('objet', q.objet);
    }
    if (q.page != null) {
      params = params.set('page', String(q.page));
    }
    if (q.page_size != null) {
      params = params.set('page_size', String(q.page_size));
    }
    return this.http.get<unknown>(`${this.base}/param-audit-log`, { params }).pipe(
      map((raw) => {
        const rows = unwrapArray<ParamAuditLogDto>(raw);
        let total: number | undefined;
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          const o = raw as Record<string, unknown>;
          total = o['total'] != null ? Number(o['total']) : undefined;
        }
        return { rows, total };
      })
    );
  }

  getDataImportFormats(stationId: number, userId: number, tableDbId: number): Observable<DataImportFormatDto[]> {
    const params = new HttpParams()
      .set('station_id', String(stationId))
      .set('user_id', String(userId))
      .set('table_db_id', String(tableDbId));
    return this.http
      .get<unknown>(`${this.base}/import-formats`, { params })
      .pipe(map((raw) => unwrapArray<DataImportFormatDto>(raw)));
  }

  postDataImportFormat(body: Partial<DataImportFormatDto>): Observable<DataImportFormatDto> {
    return this.http.post<DataImportFormatDto>(`${this.base}/import-formats`, body);
  }

  deleteDataImportFormat(importFormatId: number, stationId: number, userId: number): Observable<unknown> {
    const params = new HttpParams().set('station_id', String(stationId)).set('user_id', String(userId));
    return this.http.delete<unknown>(`${this.base}/import-formats/${encodeURIComponent(String(importFormatId))}`, {
      params
    });
  }

  getDataImportMappings(importFormatId: number): Observable<DataImportMappingDto[]> {
    const params = new HttpParams().set('import_format_id', String(importFormatId));
    return this.http
      .get<unknown>(`${this.base}/import-mappings`, { params })
      .pipe(map((raw) => unwrapArray<DataImportMappingDto>(raw)));
  }

  /** GET `/api/parametrage/import-mappings/by-import-format/:import_format_id?station_id=` */
  getDataImportMappingsByImportFormat(
    importFormatId: number,
    stationId: number
  ): Observable<DataImportMappingDto[]> {
    const params = new HttpParams().set('station_id', String(stationId));
    return this.http
      .get<unknown>(
        `${this.base}/import-mappings/by-import-format/${encodeURIComponent(String(importFormatId))}`,
        { params }
      )
      .pipe(map((raw) => unwrapArray<DataImportMappingDto>(raw)));
  }

  putDataImportMappings(importFormatId: number, rows: DataImportMappingDto[]): Observable<unknown> {
    return this.http.put<unknown>(`${this.base}/import-mappings`, {
      import_format_id: importFormatId,
      mappings: rows
    });
  }

  /** PUT `/import-formats/:id/mapping` — remplace tout le mapping d’un format d’import (même logique que Export). */
  putDataImportFormatMappings(
    importFormatId: number,
    stationId: number,
    userId: number,
    rows: Array<Pick<DataImportMappingDto, 'colonne_fichier' | 'champ_cible' | 'transformation'>>
  ): Observable<unknown> {
    const params = new HttpParams().set('station_id', String(stationId)).set('user_id', String(userId));
    return this.http.put<unknown>(
      `${this.base}/import-formats/${encodeURIComponent(String(importFormatId))}/mapping`,
      { mappings: rows },
      { params }
    );
  }

  /** POST `/import-formats/:id/mapping` — enregistre le mapping pour un format d’import */
  postDataImportMappings(
    importFormatId: number,
    stationId: number,
    rows: DataImportMappingDto[]
  ): Observable<unknown> {
    // Backend: un POST par ligne, body exemple:
    // { station_id: 1, colonne_fichier: "COL_A", champ_cible: "nom", transformation: "trim" }
    const list = Array.isArray(rows) ? rows : [];
    if (list.length === 0) {
      return of([]);
    }
    const url = `${this.base}/import-formats/${encodeURIComponent(String(importFormatId))}/mapping`;
    return forkJoin(
      list.map((r) =>
        this.http.post<unknown>(url, {
          station_id: stationId,
          colonne_fichier: (r as any).colonne_fichier,
          champ_cible: (r as any).champ_cible,
          transformation: (r as any).transformation
        })
      )
    );
  }

  postImportPreview(importFormatId: number, formData: FormData): Observable<unknown> {
    return this.http.post<unknown>(`${this.base}/import-formats/${importFormatId}/preview`, formData);
  }

  postImportSimulate(importFormatId: number, formData: FormData): Observable<unknown> {
    return this.http.post<unknown>(`${this.base}/import-formats/${importFormatId}/simulate`, formData);
  }

  getDataExportFormats(stationId: number, userId: number, tableDbId: number): Observable<DataExportFormatDto[]> {
    const params = new HttpParams()
      .set('station_id', String(stationId))
      .set('user_id', String(userId))
      .set('table_db_id', String(tableDbId));
    return this.http
      .get<unknown>(`${this.base}/export-formats`, { params })
      .pipe(map((raw) => unwrapArray<DataExportFormatDto>(raw)));
  }

  /** GET `/api/parametrage/export-formats/by-table/:table_db_id` */
  getDataExportFormatsByTable(
    tableDbId: number,
    stationId: number,
    userId: number
  ): Observable<DataExportFormatDto[]> {
    const params = new HttpParams().set('station_id', String(stationId)).set('user_id', String(userId));
    return this.http
      .get<unknown>(`${this.base}/export-formats/by-table/${encodeURIComponent(String(tableDbId))}`, { params })
      .pipe(map((raw) => unwrapArray<DataExportFormatDto>(raw)));
  }

  /** POST `/api/parametrage/export-formats` — crée un format d’export (corps + contexte session en query). */
  postDataExportFormat(
    body: Partial<DataExportFormatDto>,
    stationId: number,
    userId: number
  ): Observable<DataExportFormatDto> {
    const params = new HttpParams().set('station_id', String(stationId)).set('user_id', String(userId));
    return this.http.post<unknown>(`${this.base}/export-formats`, body, { params }).pipe(
      map((raw) => {
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          const o = raw as Record<string, unknown>;
          if (o['data'] && typeof o['data'] === 'object') {
            return o['data'] as DataExportFormatDto;
          }
        }
        return raw as DataExportFormatDto;
      })
    );
  }

  /** DELETE `/api/parametrage/export-formats/:exportFormatId` */
  deleteDataExportFormat(exportFormatId: number, stationId: number, userId: number): Observable<unknown> {
    const params = new HttpParams().set('station_id', String(stationId)).set('user_id', String(userId));
    return this.http.delete<unknown>(
      `${this.base}/export-formats/${encodeURIComponent(String(exportFormatId))}`,
      { params }
    );
  }

  getDataExportDestinations(stationId: number, userId: number, tableDbId: number): Observable<DataExportDestinationDto[]> {
    const params = new HttpParams()
      .set('station_id', String(stationId))
      .set('user_id', String(userId))
      .set('table_db_id', String(tableDbId));
    return this.http
      .get<unknown>(`${this.base}/export-destinations`, { params })
      .pipe(map((raw) => unwrapArray<DataExportDestinationDto>(raw)));
  }

  putDataExportDestination(id: number, body: Partial<DataExportDestinationDto>): Observable<unknown> {
    return this.http.put<unknown>(`${this.base}/export-destinations/${id}`, body);
  }

    postExportTest(
        exportFormatId: number,
        stationId: number,
        userId: number,
        body: Record<string, unknown>
    ): Observable<unknown> {
        const params = new HttpParams().set('station_id', String(stationId)).set('user_id', String(userId));
        return this.http.post<unknown>(`${this.base}/export-formats/${exportFormatId}/test`, body, { params });
    }

  /** POST — ajoute une ligne data_export_mapping pour ce format */
  postDataExportMapping(
    exportFormatId: number,
    stationId: number,
    userId: number,
    body: DataExportMappingWriteDto
  ): Observable<DataExportMappingDto> {
    const params = new HttpParams().set('station_id', String(stationId)).set('user_id', String(userId));
    return this.http
      .post<unknown>(`${this.base}/export-formats/${exportFormatId}/mapping`, body, { params })
      .pipe(
        map((raw) => {
          if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
            const o = raw as Record<string, unknown>;
            if (o['data'] && typeof o['data'] === 'object') {
              return o['data'] as DataExportMappingDto;
            }
          }
          return raw as DataExportMappingDto;
        })
      );
  }

  /** GET data_export_mapping pour un format d’export */
  getDataExportMappings(
    exportFormatId: number,
    stationId: number,
    userId: number
  ): Observable<DataExportMappingDto[]> {
    const params = new HttpParams().set('station_id', String(stationId)).set('user_id', String(userId));
    return this.http
      .get<unknown>(`${this.base}/export-formats/${exportFormatId}/mapping`, { params })
      .pipe(map((raw) => unwrapArray<DataExportMappingDto>(raw)));
  }

  /** GET `/api/parametrage/export-mappings/by-export-format/:export_format_id` */
  getDataExportMappingsByExportFormat(
    exportFormatId: number,
    stationId: number,
    userId: number
  ): Observable<DataExportMappingDto[]> {
    const params = new HttpParams().set('station_id', String(stationId)).set('user_id', String(userId));
    return this.http
      .get<unknown>(
        `${this.base}/export-mappings/by-export-format/${encodeURIComponent(String(exportFormatId))}`,
        { params }
      )
      .pipe(map((raw) => unwrapArray<DataExportMappingDto>(raw)));
  }

  /** PUT remplacement des lignes de mapping export */
  putDataExportMappings(
    exportFormatId: number,
    stationId: number,
    userId: number,
    mappings: DataExportMappingWriteDto[]
  ): Observable<unknown> {
    const params = new HttpParams().set('station_id', String(stationId)).set('user_id', String(userId));
    return this.http.put<unknown>(
      `${this.base}/export-formats/${exportFormatId}/mapping`,
      { mappings },
      { params }
    );
  }

  /** DELETE une ligne de mapping */
  deleteDataExportMapping(
    exportFormatId: number,
    mappingId: number,
    stationId: number,
    userId: number
  ): Observable<unknown> {
    const params = new HttpParams().set('station_id', String(stationId)).set('user_id', String(userId));
    return this.http.delete<unknown>(
      `${this.base}/export-formats/${exportFormatId}/mapping/${mappingId}`,
      { params }
    );
  }
}
